import React, { useState } from 'react';
import axios from 'axios';
import { contactData, targetAudience, audienceQualifier, audienceCTA, audienceDisclaimer, audienceTimingNote } from '../data/mock';
import { Send, CheckCircle, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Contact form submission error:', err);
      setError('Something went wrong. Please try again or email directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-[#0B0D10] py-20 lg:py-28 border-t border-white/5" aria-labelledby="contact-heading">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Who This Is For */}
          <div className="lg:pt-2" aria-labelledby="who-this-is-for-heading">
            {/* Section Title */}
            <div className="flex items-center gap-4 mb-8">
              <h2 id="who-this-is-for-heading" className="text-2xl lg:text-3xl font-semibold text-[#F2F4F7]">
                Who This Is For
              </h2>
              <div className="flex-1 h-px bg-white/10" aria-hidden="true" />
            </div>

            {/* Audience List */}
            <ul className="space-y-6 mb-6">
              {targetAudience.map((audience, index) => (
                <li key={index} className="flex gap-4">
                  <div className="w-5 h-5 rounded-sm bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0 mt-1" aria-hidden="true">
                    <Check size={14} className="text-[#3B82F6]" />
                  </div>
                  <div>
                    <span className="text-[#F2F4F7] font-medium text-base block">{audience.title}</span>
                    <span className="text-[#A6AFBD] text-sm leading-relaxed">{audience.description}</span>
                  </div>
                </li>
              ))}
            </ul>

            {/* Qualification Line */}
            <p className="text-sm text-[#A6AFBD] mb-6">
              {audienceQualifier}
            </p>

            {/* CTA Line */}
            <p className="text-sm text-[#F2F4F7] mb-3">
              {audienceCTA}
            </p>

            {/* Disclaimer */}
            <p className="text-xs text-[#A6AFBD]/60 mb-6">
              {audienceDisclaimer}
            </p>

            {/* Timing Note */}
            <p className="text-sm text-[#A6AFBD]">
              {audienceTimingNote}
            </p>
          </div>

          {/* Right: Contact Form */}
          <div>
            {isSubmitted ? (
              <div className="bg-[#11151B] border border-white/5 p-10">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3B82F6]/20 mb-5" aria-hidden="true">
                    <CheckCircle size={32} className="text-[#3B82F6]" />
                  </div>
                  <p className="text-[#A6AFBD] leading-relaxed text-base">
                    {contactData.successMessage}
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Section Title */}
                <h2 id="contact-heading" className="text-2xl lg:text-3xl font-semibold text-[#F2F4F7] mb-3">
                  {contactData.title}
                </h2>
                <p className="text-[#A6AFBD] mb-6 text-sm">
                  {contactData.description}
                </p>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4" aria-label="Contact form">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="sr-only">Full Name</label>
                    <input
                      id="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full Name"
                      required
                      aria-required="true"
                      className="w-full bg-[#11151B] border border-white/10 text-[#F2F4F7] px-4 py-3.5 text-sm placeholder:text-[#A6AFBD]/50 focus:outline-none focus:border-[#3B82F6]/50 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="sr-only">Email</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email"
                      required
                      aria-required="true"
                      className="w-full bg-[#11151B] border border-white/10 text-[#F2F4F7] px-4 py-3.5 text-sm placeholder:text-[#A6AFBD]/50 focus:outline-none focus:border-[#3B82F6]/50 transition-colors"
                    />
                  </div>

                  {/* Company (Optional) */}
                  <div>
                    <label htmlFor="company" className="sr-only">Company / Brand (Optional)</label>
                    <input
                      id="company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company / Brand (Optional)"
                      className="w-full bg-[#11151B] border border-white/10 text-[#F2F4F7] px-4 py-3.5 text-sm placeholder:text-[#A6AFBD]/50 focus:outline-none focus:border-[#3B82F6]/50 transition-colors"
                    />
                  </div>

                  {/* Phone (Optional) */}
                  <div>
                    <label htmlFor="phone" className="sr-only">Phone Number (Optional)</label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone Number (Optional)"
                      className="w-full bg-[#11151B] border border-white/10 text-[#F2F4F7] px-4 py-3.5 text-sm placeholder:text-[#A6AFBD]/50 focus:outline-none focus:border-[#3B82F6]/50 transition-colors"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="sr-only">Project Details</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Details About Your Project..."
                      rows={4}
                      required
                      aria-required="true"
                      className="w-full bg-[#11151B] border border-white/10 text-[#F2F4F7] px-4 py-3.5 text-sm placeholder:text-[#A6AFBD]/50 focus:outline-none focus:border-[#3B82F6]/50 transition-colors resize-none"
                    />
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div role="alert" className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 px-4 py-3">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className="w-full bg-[#3B82F6] text-white py-4 text-base font-medium transition-all duration-300 hover:bg-[#2563EB] hover:scale-[1.01] hover:shadow-lg hover:shadow-[#3B82F6]/25 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        {contactData.submitText}
                        <Send size={16} aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>

                {/* Post-submit message preview */}
                <p className="mt-5 text-xs text-[#A6AFBD]/50 text-center">
                  Thanks—I&apos;ll take a look and follow up shortly.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
